import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Award, Search } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { isTopRated } from "@/components/Stars";
import { SHOP } from "@/constants/testIds";
import { useSeo } from "@/lib/seo";
import KiddeeCollection from "@/pages/KiddeeCollection";

const CATS = [
  ["", "All"], ["kiddee", "Kiddee's picks"], ["treats", "Treats"], ["toys", "Toys"], ["water", "Water play"],
  ["fetch", "Fetch"], ["durable", "Durable"], ["interactive", "Puzzles"], ["squeaky", "Squeaky"],
  ["collars", "Collars"], ["harnesses", "Harnesses"], ["leashes", "Leashes"], ["accessories", "Beds & carriers"],
];

export default function ShopPage() {
  const [params] = useSearchParams();
  return params.get("cat") === "kiddee" ? <KiddeeCollection /> : <ShopGrid />;
}

function ShopGrid() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  useSeo({
    title: cat ? `${(CATS.find(([id]) => id === cat) || [cat, cat])[1]} | PetTok Shop` : "Dog & Cat Toys, Treats and Gear | PetTok Shop",
    description: "Shop Kiddee-approved dog toys, treats, collars, harnesses and beds. Free US shipping over $50 and 30-day money-back returns.",
    path: cat ? `/shop?cat=${cat}` : "/shop",
  });
  const [q, setQ] = useState(params.get("q") || "");
  const [products, setProducts] = useState(null);
  const top = params.get("top") === "1";
  const anyTop = (products || []).some((p) => isTopRated(p.rating));
  const shown = (products || []).filter((p) => !top || isTopRated(p.rating));

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { cat: cat || undefined, q: q || undefined } }).then(({ data }) => setProducts(data)).catch(() => setProducts([]));
    }, 200);
    return () => clearTimeout(t);
  }, [cat, q]);

  return (
    <main data-testid={SHOP.page} className="pt-28 pb-10 max-w-7xl mx-auto px-6 lg:px-10">
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">The shop</h1>
      <p className="mt-3 text-stone-600">Every product here got past Kiddee first.</p>
      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {(anyTop || top) && (
            <button
              data-testid={SHOP.topRatedFilter}
              onClick={() => setParams(top ? (cat ? { cat } : {}) : { ...(cat ? { cat } : {}), top: "1" })}
              aria-pressed={top}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-colors ${top ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[#E7E0D3] hover:bg-[#F0EBE1]"}`}
            >
              <Award size={14} className="text-[#F2B544]" /> Top rated
            </button>
          )}
          {CATS.map(([id, label]) => (
            <button
              key={id || "all"}
              data-testid={SHOP.categoryPill(id || "all")}
              onClick={() => setParams({ ...(id ? { cat: id } : {}), ...(top ? { top: "1" } : {}) })}
              className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${cat === id ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[#E7E0D3] hover:bg-[#F0EBE1]"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="relative shrink-0 lg:w-72">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid={SHOP.searchInput} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#E7E0D3] bg-white focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40" />
        </label>
      </div>
      {products && products.length === 0 && <p data-testid={SHOP.empty} className="mt-16 text-center text-stone-500">Nothing matches that yet.</p>}
      {top && products?.length > 0 && !shown.length && <p data-testid={SHOP.topRatedEmpty} className="mt-16 text-center text-stone-500">No top rated products here yet.</p>}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {shown.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
      </div>
    </main>
  );
}
